import React, {
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import {useNavigationState} from '../../navigation-provider'
import {flyoutNavigationRecipe} from './flyout-navigation.recipe'
import {useNavigationContent} from '../../../../../hooks/useScapiCustomApi'
import {hasNavigationTeaserContent} from '../../navigation-teaser'
import FlyoutNavigationLevel1 from './flyout-navigation-level1'
import FlyoutNavigationLevel2 from './flyout-navigation-level2'
import FlyoutNavigationLevel3, {NAVIGATION_LEVEL3} from './flyout-navigation-level3'
import FlyoutNavigationLevel3Teasers, {
    NAVIGATION_LEVEL3_TEASERS
} from './flyout-navigation-level3-teasers'
import FlyoutToolNav from './flyout-tool-nav'
import {PHASE, settled, usePhasedSwap} from './flyout-motion'
import {useLevelFocus} from './flyout-focus'

/**
 * Test handle for the tree root. Exported so a rename cannot leave the tests
 * silently querying an id nothing carries.
 */
export const FLYOUT_TREE_TESTID = 'flyout-tree'

const NAV_LVL_3_VIEW_MAP = {
    [NAVIGATION_LEVEL3]: FlyoutNavigationLevel3,
    [NAVIGATION_LEVEL3_TEASERS]: FlyoutNavigationLevel3Teasers
}

/**
 * Flyout body: the CMS-authored navigation tree.
 *
 * Levels 1 and 2 share one markup tree across both viewports — the level-2 pane
 * for the active entry is always rendered, and only the recipe decides whether
 * it is on screen (see `DRILLED_IN` in `flyout-navigation.recipe.js`). So the
 * selection here is viewport-agnostic: `selectedId` is null until the user picks
 * an entry, which desktop reads as "fall back to the first entry" and mobile as
 * "stay on the level-1 pane". The fallback entry is also marked active, so the
 * pre-selected desktop entry exposes the same state as the pane it represents.
 * No breakpoint is ever measured in JavaScript, so
 * server and client markup match and a resize preserves the open tree.
 *
 * Level 3 is the exception to "level 2 sits beside level 1": it takes the whole
 * panel on both viewports, so its pane is a sibling of the other two rather
 * than nested inside level 2, and `data-level3` hides them both. The entry that
 * opened it is held here for the same reason.
 *
 * Renders nothing until the flyout first opens and the module loads — the panel
 * lives in the always-mounted header card, so there is nowhere to put a spinner
 * that would not blanket the header.
 *
 * @component
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} [props.scrollerRef] - The flyout's
 *   scrollport, returned to the top on every level swap. Held by the shell (see
 *   `flyout/index.jsx`), which owns that element.
 * @returns {JSX.Element|null}
 */
const FlyoutNavigation = ({scrollerRef}) => {
    const recipe = useSlotRecipe({recipe: flyoutNavigationRecipe})
    const styles = useMemo(() => recipe(), [recipe])
    const {open, intent} = useNavigationState()
    // Served from the cache `Flyout` warms on the same gate (see there for why).
    const {data: level1} = useNavigationContent({}, {enabled: open || intent})
    // Drill-in state lives and dies with the mount: a closed flyout unmounts
    // once its exit has played out (see `Flyout`), so a reopen rests on the
    // first authored category — unless it interrupts the close, which keeps the
    // tree and everything on it.
    const [selectedId, setSelectedId] = useState(null)
    const [level3Entry, setLevel3Entry] = useState(null)

    // `elements` and `teasers` are separate CMS arrays and stay separate here:
    // only an element can open a level-2 pane, and only a teaser renders in the
    // teaser column. The momentvm wrapper puts each element under an `item` key,
    // so this is the one place that unwraps it.
    const elements = useMemo(
        () =>
            (level1?.elements || [])
                .map((entry) => (entry.type ? entry : entry.item))
                .filter(Boolean),
        [level1]
    )
    // A teaser an author left blank renders as an empty box, so it is dropped
    // here the same way levels 2 and 3 drop theirs (STOK-17335). Memoised
    // alongside `elements` above, which derives from the same payload.
    const teasers = useMemo(
        () => (level1?.teasers || []).filter(hasNavigationTeaserContent),
        [level1]
    )

    // Selectable entries — those that carry a level-2 module — keyed by the id
    // the CMS joins on, so the active pane is a direct lookup rather than a scan
    // per render. Per spec an id is unique (same id, same content), so keying is
    // lossless. `useNavigationContent` resolves `level2Module` onto the entries;
    // one whose id matched nothing keeps its `level2ModuleId` and gains no
    // module, so it is left out and stays unselectable.
    const parentsById = useMemo(() => {
        const byId = new Map()
        elements.forEach((item) => {
            if (item.level2ModuleId && item.level2Module) byId.set(item.level2ModuleId, item)
        })
        return byId
    }, [elements])

    // Desktop auto-selects the first entry; mobile ignores this until the user
    // picks, because `data-selection` still reads `auto`. The same fallback id
    // drives active state so the pre-selected desktop parent is announced as
    // active. A Map keeps insertion order, so the first key is the first
    // authored parent.
    const requestedId = selectedId ?? parentsById.keys().next().value

    // One id for the one pane every selectable level-1 entry opens.
    const paneId = useId()

    const containerRef = useRef(null)
    // Height the panel had before the outgoing content left, captured while it
    // is still on screen so the resize has somewhere to travel from.
    const fromHeightRef = useRef(null)

    // The panes, so a level change can carry focus into the one it opens.
    const level1Ref = useRef(null)
    const level2Ref = useRef(null)
    const level3Ref = useRef(null)

    // The control each level was opened with, kept so drilling back out returns
    // the shopper to where they left rather than to the top of the pane.
    const drillInOriginRef = useRef({level2: null, level3: null})

    // What the user has asked to see. Identity changes exactly when the level
    // does, which is what the sequence keys on.
    const requestedLevel = useMemo(
        () => ({id: requestedId, level3Entry, explicit: Boolean(selectedId)}),
        [requestedId, level3Entry, selectedId]
    )

    /**
     * Fits the panel to the incoming content. Runs between the swap and the
     * entrance, with the content already invisible — which is what makes it safe
     * to animate `height` at all.
     *
     * Sizing the tree root rather than the panel keeps this inside the one
     * component that owns the content: `body`, `panel` and the reveal grid row
     * are all content-sized above it, so they follow.
     *
     * The animation itself is `RESIZE_MOTION` in the recipe; the only thing that
     * cannot live there is the pair of measurements, since both ends are the
     * content's own height and CSS has no way to read them. They are published
     * as custom properties — the same channel `itemIndex` uses for the per-row
     * stagger — and `data-resize` arms the keyframe once both are in place.
     * Setting the attribute after the measurement is what keeps the first frame
     * honest: it is written during the commit that publishes the `resizing`
     * phase, so a selector on the phase alone would start the animation at the
     * reflow below, before either height existed.
     *
     * Written straight to the element rather than rendered, because `to` is only
     * measurable once the incoming content is committed — React could only carry
     * it in a *later* commit than the one that has to hold it. Safe because React
     * manages neither channel here: the tree root renders `css`, `layerStyle` and
     * its `data-*` state, and React only diffs what it rendered. Giving that
     * element a `style` prop or a `data-resize` of its own would hand both to
     * React, and it would clobber these.
     */
    const handleResize = useCallback(() => {
        const element = containerRef.current
        const from = fromHeightRef.current
        fromHeightRef.current = null
        if (!element || from == null) return undefined
        const to = element.getBoundingClientRect().height
        // Nothing to travel, so the phase passes straight through. jsdom
        // measures every box at 0 and lands here, which is also what keeps the
        // sequence synchronous in tests.
        if (Math.abs(to - from) < 1) return undefined
        element.style.setProperty('--nav-resize-from', `${from}px`)
        element.style.setProperty('--nav-resize-to', `${to}px`)
        element.dataset.resize = 'run'
        // Disarmed on the way out so the next level change re-arms rather than
        // finding the animation already named and declining to restart it. The
        // heights go with it: a pair left standing would let any later path that
        // arms without measuring travel to a previous level's height.
        return settled(element).finally(() => {
            delete element.dataset.resize
            element.style.removeProperty('--nav-resize-from')
            element.style.removeProperty('--nav-resize-to')
        })
    }, [])

    // Until a level has actually been on screen there is nothing to animate
    // away, so the first tree to arrive is presented outright. Without this the
    // content landing would be read as a level *change* and spend a whole
    // exit/resize/enter cycle replacing nothing.
    const hasPresentedRef = useRef(false)

    const {presented: presentedLevel, phase} = usePhasedSwap(requestedLevel, {
        contentRef: containerRef,
        onResize: handleResize,
        // Nothing to sequence while the flyout is shut either: the level snaps
        // so a reopen lands on the resting state rather than replaying a
        // transition nobody saw.
        enabled: open && hasPresentedRef.current
    })

    useLayoutEffect(() => {
        if (phase !== PHASE.EXITING) return
        fromHeightRef.current = containerRef.current?.getBoundingClientRect().height
    }, [phase])

    // The flyout scrolls as a whole, not one pane, so a level reached after
    // scrolling would otherwise open part-way down — most visibly on mobile,
    // where drilling back out of a long level-2 list landed on a level-1 pane
    // already scrolled to its end. Reset at the swap: desktop has faded the
    // outgoing content out by then, and mobile has nothing to exit so this is
    // still the same frame as the tap, before any pane has started travelling.
    useLayoutEffect(() => {
        if (phase !== PHASE.RESIZING) return
        if (scrollerRef?.current) scrollerRef.current.scrollTop = 0
    }, [phase, scrollerRef])

    useEffect(() => {
        if (presentedLevel.id !== undefined) hasPresentedRef.current = true
    }, [presentedLevel])

    // A pane mid-swap is on its way out or already invisible behind the resize,
    // so Tab must not reach into it. Tied to the phase rather than to the
    // per-row stagger: the cascade is decoration, and gating focus on it would
    // make a keyboard user wait out an animation. The control that asked for the
    // change can be inside the pane this blanks — every drill-in and drill-out
    // but the level-1 one is — so `useLevelFocus` catches the focus it drops.
    const paneSwapping = phase === PHASE.EXITING || phase === PHASE.RESIZING
    const paneInert = paneSwapping ? '' : undefined

    // The level asked for is the one on screen, and no pane is blanked: focus
    // handed over now lands in content the shopper can actually reach.
    const {begin, onFocusOut} = useLevelFocus({
        rootRef: containerRef,
        ready: !paneSwapping && presentedLevel === requestedLevel
    })

    // The level on screen lags the one requested for as long as the outgoing
    // content is still leaving.
    const activeId = presentedLevel.id
    const presentedLevel3 = presentedLevel.level3Entry
    const activeParent = parentsById.get(activeId)

    // The level-3 pane keeps its contents while it travels off-screen, so the
    // entry it was opened with outlives the drill-out that dismissed it.
    //
    // `presentedLevel3` alone is not enough. On desktop it is: the pane fades
    // out during `exiting`, while the entry is still presented. On mobile the
    // pane leaves on a CSS transition (`PANE_SLIDE`) that only starts once
    // `data-level3` clears — which is after the sequence has settled and the
    // entry has already gone, so the pane would slide out empty. Retaining the
    // last entry decouples the two: `data-level3` decides whether the pane is on
    // screen, and this decides only what is inside it.
    //
    // Nothing reachable leaks: the parked pane is `visibility: hidden` on mobile
    // and `display: none` on desktop, so it is out of the a11y tree and the tab
    // order either way, and level 2 has always stayed mounted on the same terms.
    // Layout is the one thing hiding does not settle on mobile: a pane retained
    // at full height still carries its scrollable overflow to the scroller, which
    // is why `panes` clips the block axis (STOK-17509).
    const retainedLevel3Ref = useRef(null)
    if (presentedLevel3) retainedLevel3Ref.current = presentedLevel3
    const level3Content = presentedLevel3 ?? retainedLevel3Ref.current
    const Level3Component = NAV_LVL_3_VIEW_MAP[level3Content?.nextLevel?.type]

    // Whether the change in flight adds or removes level 3, which is the one
    // level change that also takes the level-1 list and the tool-nav row off
    // screen. Latched when the sequence starts and held until the next one:
    // `presentedLevel3` alone cannot answer it, because it still reads the old
    // level all through the exit and the new one all through the entrance —
    // false at exactly the moments that content is moving. Held past `settled`
    // too, so the recipes can keep the entrance named rather than renaming it
    // and restarting it. See `LEVEL3_MOTION` in `flyout-stagger.js`.
    const level3SwapRef = useRef(false)
    if (phase === PHASE.EXITING)
        level3SwapRef.current = Boolean(presentedLevel3) !== Boolean(level3Entry)

    // Each of the four moves between levels hands focus over the same way: the
    // pane being opened is the fallback, and a drill-out prefers the control its
    // drill-in was pressed on. Desktop's category switch takes the first path
    // and pays nothing for it — the level-1 list stays on screen, so the pressed
    // control never loses focus and the hand-off never comes due.
    const selectLevel2 = useCallback(
        (id) => {
            drillInOriginRef.current.level2 = begin(level2Ref)
            setSelectedId(id)
        },
        [begin]
    )

    const drillIntoLevel3 = useCallback(
        (entry) => {
            drillInOriginRef.current.level3 = begin(level3Ref)
            setLevel3Entry(entry)
        },
        [begin]
    )

    // Drilling out clears the selection outright, which returns mobile to the
    // level-1 pane and desktop to the first entry in the same move.
    const handleBack = useCallback(() => {
        begin(level1Ref, drillInOriginRef.current.level2)
        setSelectedId(null)
    }, [begin])

    const handleLevel3Back = useCallback(() => {
        begin(level2Ref, drillInOriginRef.current.level3)
        setLevel3Entry(null)
    }, [begin])

    // Nothing to show — the content has not arrived yet, or the asset is empty
    // or carries nothing usable. Either way the tree stays out of the DOM.
    if (!elements.length && !teasers.length) {
        return null
    }

    return (
        <Box
            ref={containerRef}
            css={styles.container}
            // Both follow the level on screen rather than the one requested, so
            // the panes only start moving once the outgoing content has left.
            data-selection={presentedLevel.explicit ? 'explicit' : 'auto'}
            data-level3={presentedLevel3 ? 'true' : undefined}
            data-level3-swap={level3SwapRef.current ? 'true' : undefined}
            data-phase={phase}
            // Exception (docs/agents/review-testing.md): the tree root is a
            // layout container with no ARIA role or accessible name a test could
            // target, and the phase and resize state are published only on it —
            // data-testid is the only handle on it.
            data-testid={FLYOUT_TREE_TESTID}
            layerStyle="container.page"
            // Both belong to the focus hand-off: the root holds focus while the
            // panes swap, and every control that starts one is inside it, so a
            // single handler answers for all of them. Out of the tab order: it
            // is a place to hold focus, not a stop.
            tabIndex={-1}
            onBlur={onFocusOut}
            // Opts out of the legacy focus ring, which
            // `app/theme/foundations/styles.js` skips for a `leap` class and
            // would otherwise draw around the whole flyout while the root holds
            // focus. The rule reads the *first* class, and Chakra emits this one
            // ahead of its own.
            className="leap-flyout-tree"
        >
            <Box css={styles.panes}>
                {/* On mobile the list and the teaser column are one pane and
                    travel as one, so they are one box. The tool-nav row belongs
                    to the same view but stays outside, as the tree root's last
                    child — see `toolNavContainer` in the recipe. */}
                <FlyoutNavigationLevel1
                    ref={level1Ref}
                    elements={elements}
                    teasers={teasers}
                    selectedId={requestedId}
                    paneId={paneId}
                    onSelect={selectLevel2}
                />
                <Box id={paneId} ref={level2Ref} css={styles.level2Container} inert={paneInert}>
                    <FlyoutNavigationLevel2
                        module={activeParent?.level2Module}
                        title={activeParent?.title}
                        onBack={handleBack}
                        onDrillIn={drillIntoLevel3}
                    />
                </Box>
                <Box ref={level3Ref} css={styles.level3Container} inert={paneInert}>
                    {level3Content && Level3Component && (
                        <Level3Component
                            nextLevel={level3Content.nextLevel}
                            title={level3Content.title}
                            parentTitle={activeParent?.title}
                            onBack={handleLevel3Back}
                        />
                    )}
                </Box>
            </Box>
            <Box css={styles.toolNavContainer}>
                <FlyoutToolNav />
            </Box>
        </Box>
    )
}

FlyoutNavigation.propTypes = {
    scrollerRef: PropTypes.shape({current: PropTypes.object})
}

if (process.env.NODE_ENV !== 'production') {
    FlyoutNavigation.displayName = 'FlyoutNavigation'
}

export default FlyoutNavigation
